import React, { useContext, useState, useEffect, useRef } from "react";
import {
  Fab,
  Paper,
  Typography,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
  Slide,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import StarsIcon from "@material-ui/icons/Stars";
import AiAssistantContext, { AiInsightConfig } from "./AiAssistantContext";
import appDataProvider from "../../dataprovider";

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const USE_MOCK_DATA = false;

// Ordered list of card keys for display — cards render in this sequence.
const COCKPIT_CARD_ORDER = [
  "cockpitresponsetime",
  "cockpitrejectionrate",
  "cockpitoutofstock",
  "cockpitfleettype",
  "cockpitrequestaircraft",
  "cockpitcapability",
  "cockpitsvcunsvcpndifference",
  "cockpitserviceablereturn",
  "serviceableservicelevelcomponent",
  "unserviceableservicelevelcomponent",
  "monthlyserviceableservicelevelcomponent",
  "monthlyserviceablerequestcomponent",
  "cockpitactiveaircraftbyperiod",
  "cockpitactiveaircraft",
  "cockpitrequestedpnunsvcpndifference",
];

const CARD_DISPLAY_NAMES: Record<string, string> = {
  cockpitresponsetime: "Response Time",
  cockpitrejectionrate: "Rejection Rate",
  cockpitoutofstock: "Out of Stock",
  cockpitfleettype: "Fleet Type",
  cockpitrequestaircraft: "Request / Aircraft",
  cockpitcapability: "Capability Rate",
  cockpitsvcunsvcpndifference: "Svc & Core PN Difference",
  cockpitserviceablereturn: "Serviceable Return",
  serviceableservicelevelcomponent: "Serviceable Service Level",
  unserviceableservicelevelcomponent: "Core Unit Service Level",
  monthlyserviceableservicelevelcomponent: "Monthly Svc Service Level",
  monthlyserviceablerequestcomponent: "Monthly Pool Requests",
  cockpitactiveaircraftbyperiod: "Active Aircraft by Period",
  cockpitactiveaircraft: "Active Aircraft Distribution",
  cockpitrequestedpnunsvcpndifference: "Requested PN & Core PN Difference",
};

// ---------------------------------------------------------------------------
// Mock data — one entry per card key
// ---------------------------------------------------------------------------

interface AiSummaryResponse {
  title: string;
  insights: string[];
}

const MOCK_INSIGHTS: Record<string, AiSummaryResponse> = {
  cockpit: {
    title: "Cockpit Genel Değerlendirmesi",
    insights: [
      "Seçili dönem ve kontrat için genel performans seviyeleri incelenmiştir.",
      "Filtre aralığı boyunca hizmet sürekliliği gözlemlenmiştir.",
    ],
  },
  cockpitresponsetime: {
    title: "Yanıt Süresi Analizi",
    insights: [
      "Ortalama yanıt süresi ölçülmüş olup hedef eşiğiyle kıyaslanmıştır.",
      "İsteklerin büyük çoğunluğu hedef süre içinde yanıtlanmıştır.",
    ],
  },
  cockpitrejectionrate: {
    title: "Red Oranı Değerlendirmesi",
    insights: [
      "Red oranı hesaplanmış olup sözleşme eşikleriyle kıyaslanmıştır.",
      "Red nedenlerini incelemek için kart görünümünü kullanınız.",
    ],
  },
  cockpitoutofstock: {
    title: "Stok Dışı Analizi",
    insights: [
      "Stok dışı parça oranı hesaplanmıştır.",
      "Kritik PN'ler için tedarik planı gözden geçirilmesi önerilir.",
    ],
  },
  cockpitfleettype: {
    title: "Filo Tipi Dağılımı",
    insights: [
      "Baskın filo tipi tespit edilmiş ve dağılım analiz edilmiştir.",
      "Filo çeşitliliği bakım planlamasına hafif karmaşıklık katabilir.",
    ],
  },
  cockpitrequestaircraft: {
    title: "Talep / Uçak Oranı",
    insights: [
      "Uçak başına düşen talep oranı hesaplanmıştır.",
      "Oran, filo büyüklüğüne göre normalize edilmiş bir göstergedir.",
    ],
  },
  cockpitcapability: {
    title: "Kapasite Oranı",
    insights: [
      "Seçili dönem için kapasite oranı hesaplanmıştır.",
      "Hedefe ulaşmak için stok ve tedarik planlaması önerilir.",
    ],
  },
  cockpitsvcunsvcpndifference: {
    title: "Svc & Core PN Farkı",
    insights: [
      "Servislenebilir ve servislenemez PN'ler arasındaki fark analiz edilmiştir.",
      "Yüksek fark oranı stok optimizasyonu ihtiyacına işaret edebilir.",
    ],
  },
  cockpitserviceablereturn: {
    title: "Servislenebilir Dönüş",
    insights: [
      "Servislenebilir parça dönüş oranı hesaplanmıştır.",
      "Dönüş oranının izlenmesi stok döngüsünü iyileştirir.",
    ],
  },
  serviceableservicelevelcomponent: {
    title: "Servislenebilir Servis Seviyesi",
    insights: [
      "Zamanında ve gecikmeli teslim oranları analiz edilmiştir.",
      "Servis seviyesi sözleşme hedefleriyle karşılaştırılmıştır.",
    ],
  },
  unserviceableservicelevelcomponent: {
    title: "Servislenemez Servis Seviyesi",
    insights: [
      "Servislenemez parçalar için servis seviyesi analiz edilmiştir.",
      "Gecikme oranları ve sözleşme uyumu değerlendirilmiştir.",
    ],
  },
  monthlyserviceableservicelevelcomponent: {
    title: "Aylık Servislenebilir Servis Seviyesi",
    insights: [
      "Dönemsel servis seviyesi trendi analiz edilmiştir.",
      "Aylık dağılım iyileştirme fırsatlarını göstermektedir.",
    ],
  },
  monthlyserviceablerequestcomponent: {
    title: "Aylık Havuz Talepleri",
    insights: [
      "Aylık talep adetleri ve trendi analiz edilmiştir.",
      "Talep trendi kapasite ve planlama süreçlerini destekler.",
    ],
  },
  cockpitactiveaircraftbyperiod: {
    title: "Dönemsel Aktif Uçaklar",
    insights: [
      "Her dönemde aktif uçak sayısı hesaplanmıştır.",
      "Aktivasyon trendi filo kullanım verimliliğini gösterir.",
    ],
  },
  cockpitactiveaircraft: {
    title: "Aktif Uçak Dağılımı",
    insights: [
      "Aktif uçakların müşteri bazında dağılımı analiz edilmiştir.",
      "Baskın müşteri segmenti servis yoğunluğunu belirlemektedir.",
    ],
  },
  cockpitrequestedpnunsvcpndifference: {
    title: "İstenen PN & Core PN Farkı",
    insights: [
      "Talep edilen PN ile servislenemez PN farkı hesaplanmıştır.",
      "Bu fark, parça planlama kararlarında kritik bir göstergedir.",
    ],
  },
};

const getMockResponse = (cardKey: string): AiSummaryResponse =>
  MOCK_INSIGHTS[cardKey] ?? {
    title: `${CARD_DISPLAY_NAMES[cardKey] ?? cardKey} Summary`,
    insights: ["Analysis data for this card is ready.", "Update your filters for more detailed insights."],
  };

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CardState {
  isLoading: boolean;
  data: AiSummaryResponse | null;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles(() => ({
  /* ── FAB ─────────────────────────────────────────── */
  fabWrapper: {
    position: "fixed",
    bottom: 24,
    right: 24,
    zIndex: 1400,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 6,
  },
  fab: {
    width: 72,
    height: 72,
    backgroundColor: "#c80c0f",
    color: "#fff",
    flexDirection: "column" as const,
    "&:hover": { backgroundColor: "#a50a0c" },
    animation: "$pulse 2.8s ease-in-out infinite",
    boxShadow: "0 4px 20px rgba(200,12,15,0.45)",
  },
  fabActive: {
    animation: "none",
    backgroundColor: "#a50a0c",
    boxShadow: "0 4px 12px rgba(200,12,15,0.35)",
  },
  fabLogoWrap: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    backgroundColor: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
    animation: "$scaleAndRotate 3s ease-in-out infinite",
  },
  fabLogoWrapActive: { animation: "none" },
  fabLogo: { width: 32, height: 32, objectFit: "contain" as const },
  "@keyframes scaleAndRotate": {
    "0%, 100%": { transform: "scale(1)" },
    "50%": { transform: "scale(1.1)" },
  },
  headerLogo: {
    width: 20,
    height: 20,
    objectFit: "contain" as const,
    backgroundColor: "rgba(255,255,255,0.9)",
    borderRadius: "50%",
    padding: 2,
  },
  "@keyframes pulse": {
    "0%": { boxShadow: "0 4px 20px rgba(200,12,15,0.45), 0 0 0 0 rgba(200,12,15,0.5)" },
    "60%": { boxShadow: "0 4px 20px rgba(200,12,15,0.45), 0 0 0 14px rgba(200,12,15,0)" },
    "100%": { boxShadow: "0 4px 20px rgba(200,12,15,0.45), 0 0 0 0 rgba(200,12,15,0)" },
  },

  /* ── Popup Card ─────────────────────────────────── */
  popup: {
    position: "fixed",
    bottom: 108,
    right: 24,
    width: 390,
    maxHeight: 560,
    zIndex: 1350,
    borderRadius: 16,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 12px 40px rgba(0,0,0,0.22)",
  },
  header: {
    background: "linear-gradient(135deg, #c80c0f 0%, #e53935 100%)",
    color: "#fff",
    padding: "13px 16px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  headerIconWrap: { position: "relative" as const, display: "flex", alignItems: "center" },
  headerIcon: { fontSize: 22 },
  headerStars: { position: "absolute" as const, top: -4, right: -5, fontSize: 10, opacity: 0.9 },
  headerTitle: { flex: 1, fontWeight: 700, fontSize: 15, letterSpacing: 0.3 },
  headerSubtitle: { fontSize: 10, opacity: 0.75, fontWeight: 400, display: "block", marginTop: 1 },
  headerChip: {
    backgroundColor: "rgba(255,255,255,0.22)",
    color: "#fff",
    height: 22,
    fontSize: 11,
    fontWeight: 600,
    borderRadius: 11,
  },
  closeBtn: { color: "#fff", padding: 4, marginLeft: 4 },
  refreshBtn: { color: "#fff", padding: 4 },
  refreshing: { animation: "$spin 1s linear infinite" },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },

  body: {
    overflowY: "auto",
    flex: 1,
    padding: "12px 16px 8px",
    backgroundColor: "#fff",
    maxHeight: 440,
  },
  mockBadge: {
    display: "inline-block",
    backgroundColor: "#fff3e0",
    color: "#e65100",
    fontSize: 10,
    fontWeight: 700,
    padding: "2px 8px",
    borderRadius: 10,
    letterSpacing: 0.4,
    marginBottom: 10,
  },

  /* ── Card Section ─────────────────────────────────── */
  cardSection: { marginBottom: 14 },
  cardSectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 6,
    marginTop: 2,
  },
  cardSectionDivider: { flex: 1, height: 1, backgroundColor: "#f0f0f0" },
  cardSectionLabel: {
    fontSize: 10,
    fontWeight: 800,
    color: "#c80c0f",
    letterSpacing: 0.7,
    whiteSpace: "nowrap" as const,
    textTransform: "uppercase" as const,
  },
  insightRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 5,
    padding: "5px 8px",
    borderRadius: 8,
    "&:hover": { backgroundColor: "#fafafa" },
  },
  insightIcon: { color: "#c80c0f", fontSize: 16, marginTop: 2, flexShrink: 0 },
  insightText: { fontSize: 12.5, color: "#333", lineHeight: 1.55 },

  /* ── Skeleton ─────────────────────────────────── */
  skeletonLine: {
    height: 12,
    borderRadius: 6,
    backgroundColor: "#f0f0f0",
    marginBottom: 8,
    animation: "$shimmer 1.4s ease-in-out infinite",
  },
  "@keyframes shimmer": {
    "0%": { backgroundColor: "#f0f0f0" },
    "50%": { backgroundColor: "#e4e4e4" },
    "100%": { backgroundColor: "#f0f0f0" },
  },

  /* ── Error / Empty ─────────────────────────────── */
  errorBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "16px 16px",
    gap: 6,
    color: "#888",
    textAlign: "center",
  },
  errorIcon: { fontSize: 36, color: "#e0e0e0" },
  retryBtn: {
    marginTop: 4,
    fontSize: 12,
    color: "#c80c0f",
    cursor: "pointer",
    textDecoration: "underline",
    background: "none",
    border: "none",
    padding: 0,
  },

  /* ── Thinking ─────────────────────────────────── */
  thinkingBox: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 10,
    padding: "7px 10px",
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    borderLeft: "3px solid #c80c0f",
  },
  thinkingText: { fontSize: 12, fontWeight: 600, color: "#555", letterSpacing: 0.5 },
  thinkingDots: { display: "flex", gap: 3, alignItems: "center" },
  thinkingDot: {
    width: 4,
    height: 4,
    borderRadius: "50%",
    backgroundColor: "#c80c0f",
    animation: "$thinkingPulse 1.2s ease-in-out infinite",
  },
  "@keyframes thinkingPulse": {
    "0%, 100%": { opacity: 0.3 },
    "50%": { opacity: 1 },
  },

  /* ── Footer ─────────────────────────────────── */
  footer: {
    padding: "7px 16px 10px",
    backgroundColor: "#fafafa",
    borderTop: "1px solid #f0f0f0",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
  },
  footerText: { fontSize: 11, color: "#bbb" },
  footerDot: {
    width: 5,
    height: 5,
    borderRadius: "50%",
    backgroundColor: "#4caf50",
    display: "inline-block",
    flexShrink: 0,
    animation: "$blink 2s ease-in-out infinite",
  },
  "@keyframes blink": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.25 },
  },
}));

// ---------------------------------------------------------------------------
// Thinking Indicator
// ---------------------------------------------------------------------------

const ThinkingIndicator: React.FC = () => {
  const classes = useStyles();
  return (
    <div className={classes.thinkingBox}>
      <Typography className={classes.thinkingText}>AI is thinking.</Typography>
      <div className={classes.thinkingDots}>
        <div className={classes.thinkingDot} style={{ animationDelay: "0s" }} />
        <div className={classes.thinkingDot} style={{ animationDelay: "0.2s" }} />
        <div className={classes.thinkingDot} style={{ animationDelay: "0.4s" }} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Skeleton lines
// ---------------------------------------------------------------------------

const SkeletonLines: React.FC<{ count?: number }> = ({ count = 4 }) => {
  const classes = useStyles();
  const widths = ["88%", "72%", "82%", "60%", "78%", "68%"];
  return (
    <>
      {widths.slice(0, count).map((w, i) => (
        <div key={i} className={classes.skeletonLine} style={{ width: w, animationDelay: `${i * 0.12}s` }} />
      ))}
    </>
  );
};

// ---------------------------------------------------------------------------
// Per-card section
// ---------------------------------------------------------------------------

const CardSection: React.FC<{
  cardKey: string;
  state: CardState;
  onRetry: () => void;
}> = ({ cardKey, state, onRetry }) => {
  const classes = useStyles();
  const displayName = CARD_DISPLAY_NAMES[cardKey] ?? cardKey;

  return (
    <div className={classes.cardSection}>
      {/* Section header */}
      <div className={classes.cardSectionHeader}>
        <div className={classes.cardSectionDivider} />
        <Typography className={classes.cardSectionLabel}>{displayName}</Typography>
        <div className={classes.cardSectionDivider} />
      </div>

      {/* Loading */}
      {state.isLoading && (
        <div style={{ padding: "2px 0" }}>
          <ThinkingIndicator />
          <SkeletonLines count={3} />
        </div>
      )}

      {/* Error */}
      {!state.isLoading && state.error && (
        <div className={classes.errorBox}>
          <ErrorOutlineIcon className={classes.errorIcon} />
          <Typography style={{ fontSize: 12, color: "#999" }}>{state.error}</Typography>
          <button className={classes.retryBtn} onClick={onRetry}>
            Retry
          </button>
        </div>
      )}

      {/* Insights */}
      {!state.isLoading && !state.error && state.data && (
        <>
          {(state.data.insights ?? []).map((insight, idx) => (
            <div key={idx} className={classes.insightRow}>
              <CheckCircleOutlineIcon className={classes.insightIcon} />
              <Typography className={classes.insightText}>{insight}</Typography>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getSortedCardKeys(keys: string[]): string[] {
  return [...keys].sort((a, b) => {
    const ia = COCKPIT_CARD_ORDER.indexOf(a);
    const ib = COCKPIT_CARD_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return 0;
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

// ---------------------------------------------------------------------------
// Main Bubble component
// ---------------------------------------------------------------------------

const AiAssistantBubble: React.FC = () => {
  const classes = useStyles();
  const ctx = useContext(AiAssistantContext);

  const [isOpen, setIsOpen] = useState(false);
  const [cardStates, setCardStates] = useState<Record<string, CardState>>({});
  const [isRefreshing, setIsRefreshing] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevInsightsRef = useRef<string>("");
  const hasEverFetchedRef = useRef(false);
  const fetchGenRef = useRef(0);
  const insightsRef = useRef<AiInsightConfig[]>([]);
  insightsRef.current = ctx?.insights ?? [];

  // ── Fetch all registered insights (streaming per card for mock, allSettled for real) ──
  const fetchAllInsights = (showFullLoader: boolean) => {
    const insightsToFetch = insightsRef.current;
    if (!insightsToFetch.length) return;

    const gen = ++fetchGenRef.current;

    // Set all registered cards to loading
    setCardStates(prev => {
      const next: Record<string, CardState> = {};
      insightsToFetch.forEach(i => {
        next[i.cardKey] = {
          isLoading: true,
          data: showFullLoader ? null : (prev[i.cardKey]?.data ?? null),
          error: null,
        };
      });
      return showFullLoader ? next : { ...prev, ...next };
    });

    if (!showFullLoader) setIsRefreshing(true);

    if (USE_MOCK_DATA) {
      let remaining = insightsToFetch.length;
      insightsToFetch.forEach(ins => {
        const delay = 400 + Math.random() * 1000;
        setTimeout(() => {
          if (fetchGenRef.current !== gen) return;
          setCardStates(prev => ({
            ...prev,
            [ins.cardKey]: { isLoading: false, data: getMockResponse(ins.cardKey), error: null },
          }));
          remaining--;
          if (remaining === 0) setIsRefreshing(false);
        }, delay);
      });
      return;
    }

    // Real API — fire all in parallel, update each card when it resolves
    let remaining = insightsToFetch.length;
    insightsToFetch.forEach(ins => {
      (appDataProvider as any)
        .postAiAssistantSummary({ pageName: ins.cardKey, filterParams: ins.filterParams })
        .then((response: any) => {
          if (fetchGenRef.current !== gen) return;
          const rawData = response?.data;
          let parsed: AiSummaryResponse | null = null;
          if (Array.isArray(rawData) && rawData.length > 0) {
            parsed = {
              title: rawData[0]?.title ?? "",
              insights: rawData.map((item: any) => item.insights).filter(Boolean),
            };
          } else if (rawData?.insights) {
            parsed = rawData;
          }
          setCardStates(prev => ({
            ...prev,
            [ins.cardKey]: { isLoading: false, data: parsed, error: parsed ? null : "Summary could not be retrieved." },
          }));
        })
        .catch(() => {
          if (fetchGenRef.current !== gen) return;
          setCardStates(prev => ({
            ...prev,
            [ins.cardKey]: { isLoading: false, data: null, error: "Summary could not be retrieved." },
          }));
        })
        .finally(() => {
          remaining--;
          if (remaining === 0 && fetchGenRef.current === gen) setIsRefreshing(false);
        });
    });
  };

  // ── Retry a single card ──
  const retryCard = (cardKey: string) => {
    const insight = insightsRef.current.find(i => i.cardKey === cardKey);
    if (!insight) return;

    setCardStates(prev => ({ ...prev, [cardKey]: { isLoading: true, data: null, error: null } }));

    if (USE_MOCK_DATA) {
      setTimeout(() => {
        setCardStates(prev => ({
          ...prev,
          [cardKey]: { isLoading: false, data: getMockResponse(cardKey), error: null },
        }));
      }, 600);
      return;
    }

    (appDataProvider as any)
      .postAiAssistantSummary({ pageName: cardKey, filterParams: insight.filterParams })
      .then((response: any) => {
        const rawData = response?.data;
        let parsed: AiSummaryResponse | null = null;
        if (Array.isArray(rawData) && rawData.length > 0) {
          parsed = {
            title: rawData[0]?.title ?? "",
            insights: rawData.map((item: any) => item.insights).filter(Boolean),
          };
        } else if (rawData?.insights) {
          parsed = rawData;
        }
        setCardStates(prev => ({
          ...prev,
          [cardKey]: { isLoading: false, data: parsed, error: parsed ? null : "Summary could not be retrieved." },
        }));
      })
      .catch(() => {
        setCardStates(prev => ({
          ...prev,
          [cardKey]: { isLoading: false, data: null, error: "Summary could not be retrieved." },
        }));
      });
  };

  // ── Effect: when open/insights change → debounced fetch ──
  useEffect(() => {
    if (!isOpen) return;
    const insights = ctx?.insights ?? [];
    if (!insights.length) return;

    const currentStr = JSON.stringify(insights.map(i => ({ k: i.cardKey, p: JSON.stringify(i.filterParams) })));
    if (currentStr === prevInsightsRef.current) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    const showFullLoader = !hasEverFetchedRef.current;

    debounceRef.current = setTimeout(
      () => {
        prevInsightsRef.current = currentStr;
        hasEverFetchedRef.current = true;
        fetchAllInsights(showFullLoader);
      },
      showFullLoader ? 300 : 500
    );

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ctx?.insights]);

  // ── Effect: page change → full reset ──
  useEffect(() => {
    if (!ctx?.config?.isVisible) {
      setIsOpen(false);
      setCardStates({});
      setIsRefreshing(false);
      prevInsightsRef.current = "";
      hasEverFetchedRef.current = false;
      fetchGenRef.current++;
    }
  }, [ctx?.config?.isVisible]);

  if (!ctx?.config?.isVisible) return null;

  const pageName = ctx.config.pageName;
  const sortedKeys = getSortedCardKeys(Object.keys(cardStates));
  const anyLoading = Object.values(cardStates).some(s => s.isLoading);

  return (
    <>
      {/* ── Popup Card ─────────────────────────────────────────────────── */}
      <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
        <Paper className={classes.popup} elevation={8}>
          {/* Header */}
          <div className={classes.header}>
            <div className={classes.headerIconWrap}>
              <img src="thy.png" alt="AI" className={classes.headerLogo} />
              <StarsIcon className={classes.headerStars} />
            </div>
            <div style={{ flex: 1 }}>
              <Typography className={classes.headerTitle}>AI Assistant</Typography>
              <span className={classes.headerSubtitle}>Card-Based Smart Analysis</span>
            </div>
            <Chip label={pageName.toUpperCase()} className={classes.headerChip} size="small" />
            <IconButton
              className={classes.refreshBtn}
              size="small"
              onClick={() => {
                prevInsightsRef.current = "";
                hasEverFetchedRef.current = false;
                fetchAllInsights(false);
              }}
              title="Yenile"
              disabled={anyLoading || isRefreshing}
            >
              <AutorenewIcon fontSize="small" className={anyLoading || isRefreshing ? classes.refreshing : ""} />
            </IconButton>
            <IconButton className={classes.closeBtn} size="small" onClick={() => setIsOpen(false)} title="Kapat">
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          <Divider />

          {/* Body */}
          <div className={classes.body}>
            {USE_MOCK_DATA && <span className={classes.mockBadge}>⚠ MOCK — Demo Verisi</span>}

            {/* Empty state — no insights registered yet */}
            {sortedKeys.length === 0 && (
              <div className={classes.errorBox}>
                <Typography style={{ fontSize: 13, color: "#bbb" }}>Loading card data…</Typography>
              </div>
            )}

            {/* Per-card sections */}
            {sortedKeys.map(cardKey => (
              <CardSection
                key={cardKey}
                cardKey={cardKey}
                state={cardStates[cardKey]}
                onRetry={() => retryCard(cardKey)}
              />
            ))}
          </div>

          {/* Footer */}
          <div className={classes.footer}>
            <span className={classes.footerDot} />
            <Typography className={classes.footerText}>
              {isRefreshing || anyLoading ? "Updating…" : "Auto-updates when filters change"}
            </Typography>
          </div>
        </Paper>
      </Slide>

      {/* ── FAB ─────────────────────────────────────────────────────────── */}
      <div className={classes.fabWrapper}>
        <Fab
          className={`${classes.fab} ${isOpen ? classes.fabActive : ""}`}
          onClick={() => setIsOpen(prev => !prev)}
          title="AI Assistant — Card-Based Smart Analysis"
          style={{ borderRadius: "50%" }}
        >
          {isRefreshing || anyLoading ? (
            <CircularProgress size={28} style={{ color: "#fff" }} />
          ) : (
            <div className={`${classes.fabLogoWrap} ${isOpen ? classes.fabLogoWrapActive : ""}`}>
              <img src="thy.png" alt="AI Assistant" className={classes.fabLogo} />
            </div>
          )}
        </Fab>
      </div>
    </>
  );
};

export default AiAssistantBubble;
